"use client";

import { ClientLogoCard } from "@/components/ClientLogoCard";
import { PageBottomCta } from "@/components/PageBottomCta";
import { useLanguage } from "@/components/LanguageProvider";
import type { PageSection } from "@/lib/api";
import type { ClientLogo } from "@/lib/types";

type Props = {
  clients: ClientLogo[];
  partners: ClientLogo[];
  cmsCta?: PageSection | null;
};

export function ClientsContent({ clients, partners, cmsCta }: Props) {
  const { t, lang, dir } = useLanguage();

  const label = (item: ClientLogo) =>
    lang === "ar" && item.nameAr?.trim() ? item.nameAr.trim() : item.name;

  return (
    <div className="flex flex-col" dir={dir}>
      <header className="relative overflow-hidden border-b border-[var(--border)] bg-gradient-to-br from-[var(--background)] via-[var(--section-alt)] to-[color-mix(in_oklch,var(--primary)_8%,var(--background))] px-4 py-16 text-center md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          aria-hidden
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative mx-auto max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[var(--accent)]">
            {t("clients.badge")}
          </p>
          <h1 className="mt-3 text-balance text-4xl font-extrabold tracking-tight text-[var(--primary)] md:text-5xl">
            {t("clients.title")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-pretty text-[var(--muted-foreground)] md:text-lg">
            {t("clients.subtitle")}
          </p>
        </div>
      </header>

      {clients.length > 0 ? (
        <section
          className="bg-[var(--background)] px-4 py-[var(--section-space)] md:px-6"
          aria-labelledby="clients-heading"
        >
          <div className="mx-auto max-w-6xl">
            <div className="mb-10 text-center md:mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent)]">
                {t("clients.section.clients.badge")}
              </p>
              <h2
                id="clients-heading"
                className="mt-2 text-2xl font-extrabold text-[var(--primary)] md:text-3xl"
              >
                {t("clients.section.clients.title")}
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-[var(--muted-foreground)]">
                {t("clients.section.clients.subtitle")}
              </p>
            </div>
            <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-6">
              {clients.map((item) => (
                <li key={item.id}>
                  <ClientLogoCard item={item} displayName={label(item)} />
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      {partners.length > 0 ? (
        <section
          className="bg-[var(--section-alt)] px-4 py-[var(--section-space)] md:px-6"
          aria-labelledby="partners-heading"
        >
          <div className="mx-auto max-w-6xl">
            <div className="mb-10 text-center md:mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent)]">
                {t("clients.section.partners.badge")}
              </p>
              <h2
                id="partners-heading"
                className="mt-2 text-2xl font-extrabold text-[var(--primary)] md:text-3xl"
              >
                {t("clients.section.partners.title")}
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-[var(--muted-foreground)]">
                {t("clients.section.partners.subtitle")}
              </p>
            </div>
            <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-6">
              {partners.map((item) => (
                <li key={item.id}>
                  <ClientLogoCard item={item} displayName={label(item)} />
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <PageBottomCta
        cmsCta={cmsCta}
        fallback={{
          heading: "clients.cta.heading",
          subheading: "clients.cta.subheading",
          button: "clients.cta.button",
        }}
      />
    </div>
  );
}
